import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

const API_BASE = process.env.REACT_APP_API_URL || "";

const DEFAULT_SOURCE_TYPES = [
  "public_platform",
  "direct_survey",
  "manual_import",
  "api",
];

const SOURCE_TYPE_META = {
  public_platform: {
    label: "Public platform",
    summary:
      "Use this when the review is visible to the public and contributes directly to online reputation.",
    guidance:
      "Typical cases are Google, Booking.com or TripAdvisor. These sources normally matter for reply prioritisation and reputation monitoring.",
  },
  direct_survey: {
    label: "Direct survey",
    summary:
      "Use this for feedback collected directly by the hotel after the stay through Pilcore or another owned survey flow.",
    guidance:
      "This type is ideal for internal questionnaires, post-stay emails or private NPS-style flows. It is usually not a public review channel.",
  },
  manual_import: {
    label: "Manual import",
    summary:
      "Use this when the source exists operationally but the data will enter Pilcore by hand, CSV or Excel instead of a live connection.",
    guidance:
      "Choose this when the business will periodically upload files, copy-paste feedback or migrate historical reviews without an automated connector.",
  },
  api: {
    label: "API integration",
    summary:
      "Use this for sources that are designed to connect programmatically and should eventually feed Pilcore through an automated integration.",
    guidance:
      "This is the right choice when the source is an external system, survey tool or channel that has or will have a structured API integration.",
  },
};

const FIELD_GUIDE = [
  {
    key: "id",
    title: "id",
    storage: "Auto-generated primary key",
    purpose:
      "This is the internal numeric identifier used by the database to connect `review_sources` with reviews, imports and aggregates.",
    howToFill:
      "Do not fill it manually. Pilcore creates it automatically when you save a new source.",
    warning:
      "Treat it as immutable technical metadata. It should never be used as a business label or edited by hand.",
    example: "Example: the row may end up as `id = 7`, but that number has no product meaning outside the database.",
  },
  {
    key: "code",
    title: "code",
    storage: "Stable machine key",
    purpose:
      "This is the canonical code that backend logic, imports, filters, prompts and future integrations will rely on. It is the real identity of the source inside Pilcore.",
    howToFill:
      "Use lowercase text, no spaces, and keep it stable over time. Prefer short semantic keys with underscores when needed.",
    warning:
      "Do not rename `code` casually once reviews exist. Historical mappings, imports and future API connectors should keep pointing to the same stable key.",
    example:
      "Recommended examples: `google`, `booking`, `tripadvisor`, `internal_survey`, `manual_excel_2026`.",
  },
  {
    key: "name",
    title: "name",
    storage: "Human-readable label",
    purpose:
      "This is the display name shown to admins, dashboards and operational users. It makes the source understandable without exposing technical codes.",
    howToFill:
      "Write the clearest business label possible. Use the actual commercial name of the platform or the internal programme name.",
    warning:
      "Unlike `code`, this can evolve for readability, but it should stay recognisable for hotel staff.",
    example: "Examples: `Google Reviews`, `Booking.com`, `Internal Post-Stay Survey`.",
  },
  {
    key: "source_type",
    title: "source_type",
    storage: "Behaviour family",
    purpose:
      "This tells Pilcore what kind of origin the reviews come from. It is important because public platforms, owned surveys and imports behave differently in analysis and operations.",
    howToFill:
      "Choose the option that best describes the operational nature of the source, not just the ingestion method used this week.",
    warning:
      "This field affects future product logic. Pick the semantic family that should remain true as the module grows.",
    example:
      "Use `public_platform` for public review websites, `direct_survey` for owned questionnaires, `manual_import` for spreadsheet-fed channels, and `api` for connected systems.",
  },
  {
    key: "is_active",
    title: "is_active",
    storage: "Operational availability flag",
    purpose:
      "This controls whether the source is currently available for active use in admin workflows, imports and configuration screens.",
    howToFill:
      "Leave it enabled for current sources. Turn it off when you want to retire a source without losing its historical relationships.",
    warning:
      "If a source already has reviews or import history, deactivation is usually safer than deletion.",
    example:
      "Typical use: a historical CSV source may remain in the database but be marked inactive so nobody imports into it again.",
  },
  {
    key: "supports_api",
    title: "supports_api",
    storage: "Automation capability flag",
    purpose:
      "This indicates whether the source can realistically be connected through an API or structured integration flow.",
    howToFill:
      "Set it to `true` only when the source has or is expected to have automated programmatic ingestion. Keep it `false` for spreadsheet-only or manual processes.",
    warning:
      "This field should describe platform capability, not whether the integration has already been built.",
    example:
      "A source may be `supports_api = true` even if Pilcore has not implemented the connector yet, as long as the platform itself supports that route.",
  },
  {
    key: "supports_reply_tracking",
    title: "supports_reply_tracking",
    storage: "External reply workflow flag",
    purpose:
      "This tells Pilcore whether it should treat the source as a channel where reply status matters, for example because the hotel may answer publicly.",
    howToFill:
      "Set it to `true` only for channels where the hotel may reply and you want to track the response lifecycle. Keep it `false` for private/internal surveys.",
    warning:
      "Reply tracking should reflect operational reality. Do not enable it for channels where there is no meaningful external reply to manage.",
    example:
      "Usually `true` for public platforms like Google or TripAdvisor, and `false` for internal surveys or raw imported private feedback.",
  },
  {
    key: "created_updated_at",
    title: "created_at / updated_at",
    storage: "Audit timestamps",
    purpose:
      "These timestamps tell product and support teams when the source was created and when it was last changed.",
    howToFill:
      "You do not fill them manually. Pilcore writes them automatically.",
    warning:
      "They are audit fields only. Use them to understand the history of the catalog, not as business configuration.",
    example:
      "Helpful when checking when a source was activated, renamed or prepared for a future integration.",
  },
];

const EXAMPLE_SOURCES = [
  {
    code: "google",
    name: "Google Reviews",
    source_type: "public_platform",
    is_active: true,
    supports_api: true,
    supports_reply_tracking: true,
  },
  {
    code: "booking",
    name: "Booking.com",
    source_type: "public_platform",
    is_active: true,
    supports_api: true,
    supports_reply_tracking: true,
  },
  {
    code: "tripadvisor",
    name: "TripAdvisor",
    source_type: "public_platform",
    is_active: true,
    supports_api: false,
    supports_reply_tracking: true,
  },
  {
    code: "internal_survey",
    name: "Internal Post-Stay Survey",
    source_type: "direct_survey",
    is_active: true,
    supports_api: true,
    supports_reply_tracking: false,
  },
  {
    code: "historical_excel",
    name: "Historical Excel Import",
    source_type: "manual_import",
    is_active: false,
    supports_api: false,
    supports_reply_tracking: false,
  },
];

const defaultFormState = {
  id: null,
  code: "",
  name: "",
  source_type: "public_platform",
  is_active: true,
  supports_api: false,
  supports_reply_tracking: true,
  reviews_count: 0,
  import_runs_count: 0,
  can_be_deleted: true,
  created_at: null,
  updated_at: null,
};

function normalizeCode(rawValue) {
  return String(rawValue || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_]/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function humanizeSourceType(value) {
  if (!value) return "Not set";
  return (
    SOURCE_TYPE_META[value]?.label ||
    value
      .split("_")
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ")
  );
}

function formatTimestamp(value) {
  if (!value) return "Not available yet";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return date.toLocaleString();
}

const FIELD_GUIDE_BY_KEY = FIELD_GUIDE.reduce((acc, item) => {
  acc[item.key] = item;
  return acc;
}, {});

function getAuthConfig() {
  const token = localStorage.getItem("palantir_token");
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
}

function getErrorMessage(error, fallbackMessage) {
  return error?.response?.data?.error || fallbackMessage;
}

function ReviewSourcesAdmin() {
  const [sources, setSources] = useState([]);
  const [sourceTypeOptions, setSourceTypeOptions] = useState(DEFAULT_SOURCE_TYPES);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [feedback, setFeedback] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showFormModal, setShowFormModal] = useState(false);
  const [formState, setFormState] = useState(defaultFormState);
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteError, setDeleteError] = useState("");
  const [deleting, setDeleting] = useState(false);

  const fetchSources = async ({ keepCurrentLoading = false } = {}) => {
    if (!keepCurrentLoading) setLoading(true);

    try {
      const response = await axios.get(`${API_BASE}/api/admin/review-sources`, getAuthConfig());
      setSources(Array.isArray(response.data?.items) ? response.data.items : []);
      setSourceTypeOptions(
        Array.isArray(response.data?.source_type_options) && response.data.source_type_options.length
          ? response.data.source_type_options
          : DEFAULT_SOURCE_TYPES
      );
      setLoadError("");
    } catch (error) {
      setLoadError(getErrorMessage(error, "No se pudieron cargar las fuentes de reviews."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSources();
  }, []);

  const filteredSources = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    return sources.filter((source) => {
      if (typeFilter !== "all" && source.source_type !== typeFilter) return false;
      if (statusFilter === "active" && !source.is_active) return false;
      if (statusFilter === "inactive" && source.is_active) return false;

      if (!query) return true;

      return [
        source.id,
        source.code,
        source.name,
        source.source_type,
        source.source_type_label,
        source.supports_api ? "api integration automation" : "manual",
        source.supports_reply_tracking ? "reply response public" : "private internal",
        source.is_active ? "active" : "inactive",
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(query);
    });
  }, [searchQuery, sources, statusFilter, typeFilter]);

  const metrics = useMemo(() => {
    const active = sources.filter((item) => item.is_active).length;
    const apiReady = sources.filter((item) => item.supports_api).length;
    const replyTracking = sources.filter((item) => item.supports_reply_tracking).length;
    const withHistory = sources.filter(
      (item) => Number(item.reviews_count || 0) > 0 || Number(item.import_runs_count || 0) > 0
    ).length;

    return {
      total: sources.length,
      active,
      inactive: sources.length - active,
      apiReady,
      replyTracking,
      withHistory,
      filtered: filteredSources.length,
    };
  }, [filteredSources.length, sources]);

  const selectedSourceTypeMeta = SOURCE_TYPE_META[formState.source_type] || null;
  const isEditing = Boolean(formState.id);

  const openCreateModal = () => {
    setFormState({
      ...defaultFormState,
      source_type: sourceTypeOptions[0] || defaultFormState.source_type,
    });
    setFormError("");
    setShowFormModal(true);
  };

  const openEditModal = (source) => {
    setFormState({
      ...defaultFormState,
      ...source,
      code: String(source.code || ""),
      name: String(source.name || ""),
      source_type: source.source_type || defaultFormState.source_type,
      is_active: Boolean(source.is_active),
      supports_api: Boolean(source.supports_api),
      supports_reply_tracking: Boolean(source.supports_reply_tracking),
    });
    setFormError("");
    setShowFormModal(true);
  };

  const closeFormModal = () => {
    if (saving) return;
    setShowFormModal(false);
    setFormError("");
  };

  const openDeleteModal = (source) => {
    setDeleteTarget(source);
    setDeleteError("");
  };

  const closeDeleteModal = () => {
    if (deleting) return;
    setDeleteTarget(null);
    setDeleteError("");
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setSaving(true);
    setFormError("");

    const payload = {
      code: normalizeCode(formState.code),
      name: String(formState.name || "").trim(),
      source_type: formState.source_type,
      is_active: Boolean(formState.is_active),
      supports_api: Boolean(formState.supports_api),
      supports_reply_tracking: Boolean(formState.supports_reply_tracking),
    };

    try {
      if (isEditing) {
        await axios.put(
          `${API_BASE}/api/admin/review-sources/${formState.id}`,
          payload,
          getAuthConfig()
        );
        setFeedback("Fuente actualizada correctamente.");
      } else {
        await axios.post(`${API_BASE}/api/admin/review-sources`, payload, getAuthConfig());
        setFeedback("Fuente creada correctamente.");
      }

      setShowFormModal(false);
      await fetchSources({ keepCurrentLoading: true });
    } catch (error) {
      setFormError(
        getErrorMessage(error, "No se pudo guardar la fuente. Revisa el formulario.")
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (event) => {
    event.preventDefault();
    if (!deleteTarget?.id) return;

    setDeleting(true);
    setDeleteError("");

    try {
      await axios.delete(
        `${API_BASE}/api/admin/review-sources/${deleteTarget.id}`,
        getAuthConfig()
      );
      setFeedback("Fuente eliminada correctamente.");
      setDeleteTarget(null);
      await fetchSources({ keepCurrentLoading: true });
    } catch (error) {
      setDeleteError(
        getErrorMessage(
          error,
          "No se pudo eliminar la fuente. Si tiene historia, desactívala."
        )
      );
    } finally {
      setDeleting(false);
    }
  };

  const uiCss = `
    .rs-page{width:100%;max-width:100%;margin:0 auto;padding:0 18px 16px;height:100%;min-height:0;box-sizing:border-box;overflow:auto}
    .rs-stack{display:flex;flex-direction:column;gap:16px;min-height:100%}
    .rs-card{position:relative;border-radius:18px;border:1px solid transparent;background:linear-gradient(180deg,rgba(25,25,51,.94),rgba(10,10,22,.94)) padding-box,linear-gradient(135deg,rgba(48,216,255,.55),rgba(168,85,247,.65),rgba(255,77,79,.45)) border-box;box-shadow:0 28px 90px rgba(0,0,0,.58);overflow:hidden}
    .rs-card:before{content:"";position:absolute;inset:0;background:radial-gradient(900px 300px at 10% 0%,rgba(48,216,255,.14),transparent 60%),radial-gradient(700px 260px at 90% 10%,rgba(168,85,247,.16),transparent 62%),radial-gradient(800px 260px at 40% 110%,rgba(255,77,79,.10),transparent 60%);pointer-events:none;opacity:.95}
    .rs-card>*{position:relative;z-index:1}
    .rs-cardHeader{display:flex;justify-content:space-between;align-items:flex-end;gap:16px;padding:18px 18px 14px;border-bottom:1px solid rgba(255,255,255,.08)}
    .rs-title{margin:0;font-size:1.35rem;font-weight:800;color:transparent;background:linear-gradient(90deg,#e6e6f7 0%,rgba(48,216,255,.95) 40%,rgba(168,85,247,.95) 85%);-webkit-background-clip:text;background-clip:text}
    .rs-subtitle{margin-top:4px;font-size:.88rem;color:rgba(230,230,247,.72);line-height:1.58;max-width:940px}
    .rs-toolbar{display:flex;justify-content:flex-end;align-items:center;gap:10px;flex-wrap:wrap}
    .rs-body{padding:18px}
    .rs-banner{margin:14px 18px 0;padding:12px 14px;border-radius:14px;font-size:.9rem;line-height:1.55}
    .rs-banner--success{border:1px solid rgba(34,197,94,.32);background:rgba(34,197,94,.12);color:rgba(226,255,236,.92)}
    .rs-banner--error{border:1px solid rgba(255,77,79,.35);background:rgba(255,77,79,.12);color:rgba(255,228,230,.92)}
    .rs-metricsGrid,.rs-guideGrid,.rs-typeGrid,.rs-twoCol,.rs-checkGrid{display:grid;gap:12px}
    .rs-metricsGrid{grid-template-columns:repeat(5,minmax(0,1fr))}
    .rs-twoCol{grid-template-columns:minmax(0,1.2fr) minmax(300px,.8fr)}
    .rs-guideGrid,.rs-typeGrid{grid-template-columns:repeat(2,minmax(0,1fr))}
    .rs-checkGrid{grid-template-columns:repeat(3,minmax(0,1fr))}
    .rs-metricCard,.rs-guideCard,.rs-typeCard,.rs-helperItem,.rs-field,.rs-checkCard{border-radius:16px;border:1px solid rgba(255,255,255,.08);background:rgba(255,255,255,.04);padding:16px}
    .rs-metricLabel,.rs-filterLabel,.rs-guideMeta{font-size:.76rem;text-transform:uppercase;letter-spacing:.09em;color:rgba(230,230,247,.54)}
    .rs-metricValue{margin-top:10px;font-size:2rem;font-weight:800;color:rgba(230,230,247,.96);line-height:1}
    .rs-metricHelp,.rs-guideText,.rs-helperItem,.rs-fieldHelp,.rs-checkHelp,.rs-tableMeta{margin-top:10px;font-size:.84rem;color:rgba(230,230,247,.72);line-height:1.6}
    .rs-filters{display:flex;gap:12px;flex-wrap:wrap;margin-bottom:14px}
    .rs-filterGroup{flex:1 1 220px;min-width:180px}
    .rs-input,.rs-select{width:100%;background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.10);color:rgba(230,230,247,.92);border-radius:12px;padding:10px 12px;outline:none}
    .rs-input:focus,.rs-select:focus{background:rgba(255,255,255,.06);border-color:rgba(48,216,255,.55);box-shadow:0 0 0 4px rgba(48,216,255,.12)}
    .rs-input::placeholder{color:rgba(230,230,247,.45)}
    .rs-btn{border-radius:12px;padding:10px 14px;font-size:.93rem;font-weight:700;border:1px solid transparent;cursor:pointer;transition:transform .12s ease;background:rgba(255,255,255,.05);color:rgba(230,230,247,.92)}
    .rs-btn:disabled{opacity:.55;cursor:not-allowed;transform:none}
    .rs-btn:hover:not(:disabled){transform:translateY(-1px)}
    .rs-btn--primary{border-color:rgba(48,216,255,.55);background:linear-gradient(135deg,rgba(48,216,255,.18),rgba(168,85,247,.30))}
    .rs-btn--ghost{border-color:rgba(255,255,255,.16)}
    .rs-btn--danger{border-color:rgba(255,77,79,.6);background:linear-gradient(135deg,rgba(255,77,79,.14),rgba(168,85,247,.22))}
    .rs-tableWrap,.rs-exampleWrap{overflow:auto;border-radius:16px;border:1px solid rgba(255,255,255,.08);background:rgba(6,6,14,.38)}
    .rs-table,.rs-exampleTable{width:100%;border-collapse:separate;border-spacing:0}
    .rs-table{min-width:980px}
    .rs-exampleTable{min-width:720px}
    .rs-table thead th,.rs-exampleTable th{position:sticky;top:0;z-index:2;padding:14px;text-align:left;font-size:.78rem;text-transform:uppercase;letter-spacing:.09em;color:rgba(230,230,247,.64);background:linear-gradient(180deg,rgba(22,22,50,.96),rgba(12,12,28,.96));border-bottom:1px solid rgba(255,255,255,.08)}
    .rs-table tbody td,.rs-exampleTable td{padding:14px;border-bottom:1px solid rgba(255,255,255,.06);vertical-align:top;color:rgba(230,230,247,.88);font-size:.92rem}
    .rs-table tbody tr:hover td,.rs-exampleTable tbody tr:hover td{background:rgba(255,255,255,.025)}
    .rs-pill{display:inline-flex;align-items:center;gap:6px;padding:5px 10px;border-radius:999px;font-size:.76rem;font-weight:700;border:1px solid rgba(255,255,255,.12);background:rgba(255,255,255,.05);color:rgba(230,230,247,.92)}
    .rs-pill--id{color:rgba(48,216,255,.94);border-color:rgba(48,216,255,.28);background:rgba(48,216,255,.10)}
    .rs-pill--success{color:rgba(187,247,208,.96);border-color:rgba(34,197,94,.3);background:rgba(34,197,94,.12)}
    .rs-pill--muted{color:rgba(226,232,240,.88);border-color:rgba(148,163,184,.28);background:rgba(148,163,184,.10)}
    .rs-pill--warning{color:rgba(254,240,138,.96);border-color:rgba(250,204,21,.3);background:rgba(250,204,21,.12)}
    .rs-codeBtn{border:none;background:transparent;padding:0;font-size:.98rem;font-weight:800;color:rgba(48,216,255,.96);cursor:pointer;text-align:left}
    .rs-codeBtn:hover{color:rgba(120,227,255,.98)}
    .rs-capabilities,.rs-statusCell,.rs-usageStack,.rs-rowActions{display:flex;gap:8px;flex-wrap:wrap}
    .rs-emptyState{padding:28px;text-align:center;color:rgba(230,230,247,.72);line-height:1.6}
    .rs-guideTitle,.rs-typeTitle{margin:0;font-size:1rem;font-weight:800;color:rgba(230,230,247,.94)}
    .rs-guideBlock{margin-top:12px}
    .rs-guideLabel{font-size:.78rem;font-weight:800;color:rgba(48,216,255,.88);text-transform:uppercase;letter-spacing:.08em;margin-bottom:4px}
    .rs-field{display:flex;flex-direction:column}
    .rs-fieldLabel{display:block;margin-bottom:8px;font-size:.9rem;font-weight:800;color:rgba(230,230,247,.95)}
    .rs-fieldWarning{margin-top:8px;color:rgba(254,240,138,.9);font-size:.82rem;line-height:1.5}
    .rs-readOnly{border-radius:12px;border:1px solid rgba(255,255,255,.08);background:rgba(255,255,255,.03);padding:10px 12px;min-height:44px;display:flex;align-items:center;color:rgba(230,230,247,.86)}
    .rs-formGrid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:16px}
    .rs-field--full{grid-column:1/-1}
    .rs-checkHeader{display:flex;align-items:center;gap:10px;font-weight:800;color:rgba(230,230,247,.94);margin-bottom:8px}
    .rs-checkHeader input{width:18px;height:18px;accent-color:rgba(48,216,255,.95)}
    .rs-modalOverlay{position:fixed;inset:0;background:rgba(3,5,12,.72);backdrop-filter:blur(6px);display:flex;align-items:center;justify-content:center;padding:20px;z-index:1100}
    .rs-modal{width:min(1080px,100%);max-height:calc(100vh - 40px);overflow:auto;border-radius:20px;border:1px solid transparent;background:linear-gradient(180deg,rgba(25,25,51,.98),rgba(10,10,22,.98)) padding-box,linear-gradient(135deg,rgba(48,216,255,.55),rgba(168,85,247,.65),rgba(255,77,79,.45)) border-box;box-shadow:0 32px 90px rgba(0,0,0,.6);color:rgba(230,230,247,.92)}
    .rs-modalHeader{display:flex;justify-content:space-between;align-items:flex-start;gap:16px;padding:20px 20px 16px;border-bottom:1px solid rgba(255,255,255,.08)}
    .rs-modalTitle{margin:0;font-size:1.2rem;font-weight:800;color:rgba(230,230,247,.96)}
    .rs-modalSubtitle{margin-top:6px;font-size:.88rem;line-height:1.55;color:rgba(230,230,247,.68);max-width:760px}
    .rs-modalClose{width:38px;height:38px;border-radius:12px;border:1px solid rgba(255,255,255,.12);background:rgba(255,255,255,.04);color:rgba(230,230,247,.86);font-size:1.5rem;cursor:pointer}
    .rs-modalBody{padding:20px;display:flex;flex-direction:column;gap:18px}
    .rs-modalActions{display:flex;justify-content:flex-end;gap:10px;flex-wrap:wrap}
    @media (max-width:1280px){.rs-metricsGrid{grid-template-columns:repeat(3,minmax(0,1fr))}}
    @media (max-width:1160px){.rs-twoCol{grid-template-columns:1fr}}
    @media (max-width:900px){.rs-cardHeader{flex-direction:column;align-items:stretch}.rs-formGrid,.rs-guideGrid,.rs-typeGrid,.rs-checkGrid{grid-template-columns:1fr}}
    @media (max-width:768px){.rs-page{padding:0 12px 12px;height:100%}.rs-metricsGrid{grid-template-columns:repeat(2,minmax(0,1fr))}}
    @media (max-width:520px){.rs-metricsGrid{grid-template-columns:1fr}}
  `;

  return (
    <div className="rs-page">
      <style>{uiCss}</style>

      <div className="rs-stack">
        <section className="rs-card">
          <div className="rs-cardHeader">
            <div>
              <h1 className="rs-title">Review Sources</h1>
              <div className="rs-subtitle">
                Catálogo maestro de orígenes de reseñas dentro de Reviews Intelligence. Define qué
                canales existen en PMS, cómo se identifican en base de datos y qué capacidades
                operativas tendrán antes de importar reviews, encuestas o integraciones.
              </div>
            </div>
            <div className="rs-toolbar">
              <button type="button" className="rs-btn rs-btn--ghost" onClick={() => fetchSources()}>
                Refresh
              </button>
              <button type="button" className="rs-btn rs-btn--primary" onClick={openCreateModal}>
                New source
              </button>
            </div>
          </div>

          {feedback ? <div className="rs-banner rs-banner--success">{feedback}</div> : null}
          {loadError ? <div className="rs-banner rs-banner--error">{loadError}</div> : null}

          <div className="rs-body">
            <div className="rs-metricsGrid">
              <div className="rs-metricCard">
                <div className="rs-metricLabel">Total</div>
                <div className="rs-metricValue">{metrics.total}</div>
                <div className="rs-metricHelp">
                  Total de fuentes creadas para el catálogo reputacional.
                </div>
              </div>
              <div className="rs-metricCard">
                <div className="rs-metricLabel">Active</div>
                <div className="rs-metricValue">{metrics.active}</div>
                <div className="rs-metricHelp">Fuentes disponibles para uso actual.</div>
              </div>
              <div className="rs-metricCard">
                <div className="rs-metricLabel">Inactive</div>
                <div className="rs-metricValue">{metrics.inactive}</div>
                <div className="rs-metricHelp">
                  Canales retirados pero mantenidos por trazabilidad.
                </div>
              </div>
              <div className="rs-metricCard">
                <div className="rs-metricLabel">API capable</div>
                <div className="rs-metricValue">{metrics.apiReady}</div>
                <div className="rs-metricHelp">
                  Plataformas con capacidad razonable de automatización.
                </div>
              </div>
              <div className="rs-metricCard">
                <div className="rs-metricLabel">Reply tracking</div>
                <div className="rs-metricValue">{metrics.replyTracking}</div>
                <div className="rs-metricHelp">
                  Canales donde conviene controlar respuestas externas.
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="rs-card">
          <div className="rs-cardHeader">
            <div>
              <h2 className="rs-title">Catalog maintenance</h2>
              <div className="rs-subtitle">
                Usa esta tabla para crear, editar o desactivar fuentes. La decisión más importante
                es el <code>code</code>: será la identidad estable que usarán importaciones, filtros,
                futuras APIs y lógica analítica.
              </div>
            </div>
            <div className="rs-toolbar">
              <span className="rs-pill rs-pill--id">{metrics.filtered} visible</span>
              <span className="rs-pill rs-pill--warning">{metrics.withHistory} with history</span>
            </div>
          </div>

          <div className="rs-body">
            <div className="rs-filters">
              <div className="rs-filterGroup" style={{ flexBasis: "320px" }}>
                <label className="rs-filterLabel">Search</label>
                <input
                  className="rs-input"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder="Search by code, name, type or capability..."
                />
              </div>
              <div className="rs-filterGroup">
                <label className="rs-filterLabel">Source type</label>
                <select
                  className="rs-select"
                  value={typeFilter}
                  onChange={(event) => setTypeFilter(event.target.value)}
                >
                  <option value="all">All types</option>
                  {sourceTypeOptions.map((option) => (
                    <option key={option} value={option}>
                      {humanizeSourceType(option)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="rs-filterGroup">
                <label className="rs-filterLabel">Status</label>
                <select
                  className="rs-select"
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value)}
                >
                  <option value="all">All statuses</option>
                  <option value="active">Only active</option>
                  <option value="inactive">Only inactive</option>
                </select>
              </div>
            </div>

            <div className="rs-tableWrap">
              <table className="rs-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Code & name</th>
                    <th>Source type</th>
                    <th>Capabilities</th>
                    <th>Status</th>
                    <th>Historical usage</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="7">
                        <div className="rs-emptyState">Loading review sources...</div>
                      </td>
                    </tr>
                  ) : filteredSources.length === 0 ? (
                    <tr>
                      <td colSpan="7">
                        <div className="rs-emptyState">
                          No hay fuentes que coincidan con los filtros actuales.
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredSources.map((source) => (
                      <tr key={source.id}>
                        <td>
                          <span className="rs-pill rs-pill--id">#{source.id}</span>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="rs-codeBtn"
                            onClick={() => openEditModal(source)}
                          >
                            {source.code}
                          </button>
                          <div className="rs-tableMeta">{source.name}</div>
                        </td>
                        <td>
                          <span className="rs-pill">{source.source_type_label}</span>
                          <div className="rs-tableMeta">
                            {SOURCE_TYPE_META[source.source_type]?.summary ||
                              "Tipo disponible sin explicación ampliada."}
                          </div>
                        </td>
                        <td>
                          <div className="rs-capabilities">
                            <span
                              className={`rs-pill ${
                                source.supports_api ? "rs-pill--success" : "rs-pill--muted"
                              }`}
                            >
                              {source.supports_api ? "API capable" : "No API"}
                            </span>
                            <span
                              className={`rs-pill ${
                                source.supports_reply_tracking
                                  ? "rs-pill--success"
                                  : "rs-pill--muted"
                              }`}
                            >
                              {source.supports_reply_tracking
                                ? "Reply tracking"
                                : "No reply tracking"}
                            </span>
                          </div>
                        </td>
                        <td>
                          <div className="rs-statusCell">
                            <span
                              className={`rs-pill ${
                                source.is_active ? "rs-pill--success" : "rs-pill--warning"
                              }`}
                            >
                              {source.is_active ? "Active" : "Inactive"}
                            </span>
                          </div>
                          <div className="rs-tableMeta">
                            Updated {formatTimestamp(source.updated_at)}
                          </div>
                        </td>
                        <td>
                          <div className="rs-usageStack">
                            <span className="rs-pill">{source.reviews_count || 0} reviews</span>
                            <span className="rs-pill">
                              {source.import_runs_count || 0} import runs
                            </span>
                            <span
                              className={`rs-pill ${
                                source.can_be_deleted ? "rs-pill--success" : "rs-pill--warning"
                              }`}
                            >
                              {source.can_be_deleted ? "Deletable" : "Keep for history"}
                            </span>
                          </div>
                          <div className="rs-tableMeta">
                            Created {formatTimestamp(source.created_at)}
                          </div>
                        </td>
                        <td>
                          <div className="rs-rowActions">
                            <button
                              type="button"
                              className="rs-btn rs-btn--ghost"
                              onClick={() => openEditModal(source)}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              className="rs-btn rs-btn--danger"
                              onClick={() => openDeleteModal(source)}
                              disabled={!source.can_be_deleted}
                              title={
                                source.can_be_deleted
                                  ? "Delete source"
                                  : "This source has historical usage. Deactivate it instead."
                              }
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <div className="rs-twoCol">
          <section className="rs-card">
            <div className="rs-cardHeader">
              <div>
                <h2 className="rs-title">How to feed this table correctly</h2>
                <div className="rs-subtitle">
                  Este catálogo condiciona importaciones, filtros, agregados, reply tracking y
                  futuras integraciones. Conviene alimentarlo con criterio de modelo de datos, no
                  solo con criterio visual.
                </div>
              </div>
            </div>
            <div className="rs-body">
              <div className="rs-helperItem">
                <strong>1. Crea una fila por origen real.</strong> Google, Booking, TripAdvisor o una
                encuesta interna son fuentes distintas. No crees una fuente nueva por cada fichero
                mensual o por cada importación puntual.
              </div>
              <div className="rs-helperItem">
                <strong>2. El code es la identidad técnica.</strong> Debe ser corto, estable y
                consistente porque backend, imports, reglas y filtros dependerán de él.
              </div>
              <div className="rs-helperItem">
                <strong>3. El name es la etiqueta humana.</strong> Sirve para que el equipo entienda
                el origen sin exponer claves técnicas. Se puede mejorar, pero no sustituye al
                <code> code </code> como referencia estable.
              </div>
              <div className="rs-helperItem">
                <strong>4. Source type describe la naturaleza del canal.</strong> Si hoy cargas
                Booking por Excel, sigue siendo una fuente pública. No dejes que el método de
                ingestión temporal distorsione el tipo real.
              </div>
              <div className="rs-helperItem">
                <strong>5. Desactiva antes de borrar.</strong> Si ya hay reviews o import runs
                ligados, la decisión correcta es marcar <code>is_active = false</code> para mantener
                la trazabilidad histórica.
              </div>
              <div className="rs-helperItem">
                <strong>6. Marca supports_api por capacidad de la plataforma.</strong> No por si la
                integración existe hoy. La idea es capturar el potencial real del canal.
              </div>
              <div className="rs-helperItem">
                <strong>7. Activa reply tracking solo cuando el hotel pueda responder de verdad.</strong>
                Para encuestas privadas o feedback interno, normalmente debe quedar desactivado.
              </div>
            </div>
          </section>

          <section className="rs-card">
            <div className="rs-cardHeader">
              <div>
                <h2 className="rs-title">Source type semantics</h2>
                <div className="rs-subtitle">
                  Esta clasificación será utilizada por producto y por lógica analítica para tratar
                  distinto cada origen.
                </div>
              </div>
            </div>
            <div className="rs-body">
              <div className="rs-typeGrid">
                {sourceTypeOptions.map((typeCode) => {
                  const meta = SOURCE_TYPE_META[typeCode];

                  return (
                    <article key={typeCode} className="rs-typeCard">
                      <h3 className="rs-typeTitle">{humanizeSourceType(typeCode)}</h3>
                      <div className="rs-guideMeta">{typeCode}</div>
                      <div className="rs-guideBlock">
                        <div className="rs-guideLabel">What it means</div>
                        <div className="rs-guideText">
                          {meta?.summary || "Tipo disponible sin explicación ampliada todavía."}
                        </div>
                      </div>
                      <div className="rs-guideBlock">
                        <div className="rs-guideLabel">How to use it</div>
                        <div className="rs-guideText">
                          {meta?.guidance ||
                            "Úsalo cuando la naturaleza operativa del canal encaje con esta familia."}
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>
          </section>
        </div>

        <section className="rs-card">
          <div className="rs-cardHeader">
            <div>
              <h2 className="rs-title">Field-by-field guide</h2>
              <div className="rs-subtitle">
                Explicación detallada de para qué sirve cada campo y cómo alimentarlo para que el
                catálogo escale bien cuando entren nuevas fuentes o integraciones.
              </div>
            </div>
          </div>
          <div className="rs-body">
            <div className="rs-guideGrid">
              {FIELD_GUIDE.map((field) => (
                <article key={field.key} className="rs-guideCard">
                  <h3 className="rs-guideTitle">{field.title}</h3>
                  <div className="rs-guideMeta">{field.storage}</div>
                  <div className="rs-guideBlock">
                    <div className="rs-guideLabel">Purpose</div>
                    <div className="rs-guideText">{field.purpose}</div>
                  </div>
                  <div className="rs-guideBlock">
                    <div className="rs-guideLabel">How to feed it</div>
                    <div className="rs-guideText">{field.howToFill}</div>
                  </div>
                  <div className="rs-guideBlock">
                    <div className="rs-guideLabel">What to avoid</div>
                    <div className="rs-guideText">{field.warning}</div>
                  </div>
                  <div className="rs-guideBlock">
                    <div className="rs-guideLabel">Concrete example</div>
                    <div className="rs-guideText">{field.example}</div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="rs-card">
          <div className="rs-cardHeader">
            <div>
              <h2 className="rs-title">Recommended starter examples</h2>
              <div className="rs-subtitle">
                Ejemplos orientativos para arrancar el catálogo con convenciones coherentes.
              </div>
            </div>
          </div>
          <div className="rs-body">
            <div className="rs-exampleWrap">
              <table className="rs-exampleTable">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Name</th>
                    <th>Source type</th>
                    <th>Active</th>
                    <th>API</th>
                    <th>Reply tracking</th>
                  </tr>
                </thead>
                <tbody>
                  {EXAMPLE_SOURCES.map((source) => (
                    <tr key={source.code}>
                      <td>{source.code}</td>
                      <td>{source.name}</td>
                      <td>{humanizeSourceType(source.source_type)}</td>
                      <td>{source.is_active ? "Yes" : "No"}</td>
                      <td>{source.supports_api ? "Yes" : "No"}</td>
                      <td>{source.supports_reply_tracking ? "Yes" : "No"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      </div>

      {showFormModal ? (
        <div className="rs-modalOverlay" onClick={closeFormModal}>
          <div className="rs-modal" onClick={(event) => event.stopPropagation()}>
            <div className="rs-modalHeader">
              <div>
                <h3 className="rs-modalTitle">
                  {isEditing ? "Edit review source" : "Create review source"}
                </h3>
                <div className="rs-modalSubtitle">
                  Define la identidad estable de la fuente antes de usarla en imports, APIs, surveys
                  o dashboards. Cada decisión aquí impacta trazabilidad, filtros y automatismos.
                </div>
              </div>
              <button type="button" className="rs-modalClose" onClick={closeFormModal}>
                ×
              </button>
            </div>

            <form onSubmit={handleSave}>
              <div className="rs-modalBody">
                {formError ? <div className="rs-banner rs-banner--error">{formError}</div> : null}

                <div className="rs-formGrid">
                  <div className="rs-field">
                    <label className="rs-fieldLabel">ID</label>
                    <div className="rs-readOnly">
                      {formState.id ? `#${formState.id}` : "Se asignará automáticamente al guardar."}
                    </div>
                    <div className="rs-fieldHelp">{FIELD_GUIDE_BY_KEY.id.howToFill}</div>
                    <div className="rs-fieldWarning">{FIELD_GUIDE_BY_KEY.id.warning}</div>
                  </div>

                  <div className="rs-field">
                    <label className="rs-fieldLabel">created_at / updated_at</label>
                    <div className="rs-readOnly">
                      {formState.created_at
                        ? `Created ${formatTimestamp(formState.created_at)} | Updated ${formatTimestamp(
                            formState.updated_at
                          )}`
                        : "Pilcore rellenará ambos timestamps automáticamente."}
                    </div>
                    <div className="rs-fieldHelp">
                      {FIELD_GUIDE_BY_KEY.created_updated_at.howToFill}
                    </div>
                    <div className="rs-fieldWarning">
                      {FIELD_GUIDE_BY_KEY.created_updated_at.warning}
                    </div>
                  </div>

                  <div className="rs-field">
                    <label className="rs-fieldLabel" htmlFor="review-source-code">
                      code
                    </label>
                    <input
                      id="review-source-code"
                      className="rs-input"
                      value={formState.code}
                      onChange={(event) =>
                        setFormState((current) => ({
                          ...current,
                          code: normalizeCode(event.target.value),
                        }))
                      }
                      placeholder="google"
                      required
                    />
                    <div className="rs-fieldHelp">{FIELD_GUIDE_BY_KEY.code.purpose}</div>
                    <div className="rs-fieldHelp">{FIELD_GUIDE_BY_KEY.code.howToFill}</div>
                    <div className="rs-fieldWarning">{FIELD_GUIDE_BY_KEY.code.warning}</div>
                  </div>

                  <div className="rs-field">
                    <label className="rs-fieldLabel" htmlFor="review-source-name">
                      name
                    </label>
                    <input
                      id="review-source-name"
                      className="rs-input"
                      value={formState.name}
                      onChange={(event) =>
                        setFormState((current) => ({ ...current, name: event.target.value }))
                      }
                      placeholder="Google Reviews"
                      required
                    />
                    <div className="rs-fieldHelp">{FIELD_GUIDE_BY_KEY.name.purpose}</div>
                    <div className="rs-fieldHelp">{FIELD_GUIDE_BY_KEY.name.howToFill}</div>
                    <div className="rs-fieldWarning">{FIELD_GUIDE_BY_KEY.name.warning}</div>
                  </div>

                  <div className="rs-field rs-field--full">
                    <label className="rs-fieldLabel" htmlFor="review-source-type">
                      source_type
                    </label>
                    <select
                      id="review-source-type"
                      className="rs-select"
                      value={formState.source_type}
                      onChange={(event) =>
                        setFormState((current) => ({
                          ...current,
                          source_type: event.target.value,
                        }))
                      }
                    >
                      {sourceTypeOptions.map((option) => (
                        <option key={option} value={option}>
                          {humanizeSourceType(option)}
                        </option>
                      ))}
                    </select>
                    <div className="rs-fieldHelp">{FIELD_GUIDE_BY_KEY.source_type.purpose}</div>
                    <div className="rs-fieldHelp">{FIELD_GUIDE_BY_KEY.source_type.howToFill}</div>
                    <div className="rs-fieldWarning">{FIELD_GUIDE_BY_KEY.source_type.warning}</div>
                    {selectedSourceTypeMeta ? (
                      <div className="rs-helperItem" style={{ marginTop: "12px" }}>
                        <strong>{selectedSourceTypeMeta.label}.</strong>{" "}
                        {selectedSourceTypeMeta.summary} {selectedSourceTypeMeta.guidance}
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="rs-checkGrid">
                  <div className="rs-checkCard">
                    <label className="rs-checkHeader">
                      <input
                        type="checkbox"
                        checked={Boolean(formState.is_active)}
                        onChange={(event) =>
                          setFormState((current) => ({
                            ...current,
                            is_active: event.target.checked,
                          }))
                        }
                      />
                      <span>is_active</span>
                    </label>
                    <div className="rs-checkHelp">{FIELD_GUIDE_BY_KEY.is_active.purpose}</div>
                    <div className="rs-checkHelp">{FIELD_GUIDE_BY_KEY.is_active.howToFill}</div>
                    <div className="rs-checkHelp">{FIELD_GUIDE_BY_KEY.is_active.warning}</div>
                  </div>

                  <div className="rs-checkCard">
                    <label className="rs-checkHeader">
                      <input
                        type="checkbox"
                        checked={Boolean(formState.supports_api)}
                        onChange={(event) =>
                          setFormState((current) => ({
                            ...current,
                            supports_api: event.target.checked,
                          }))
                        }
                      />
                      <span>supports_api</span>
                    </label>
                    <div className="rs-checkHelp">{FIELD_GUIDE_BY_KEY.supports_api.purpose}</div>
                    <div className="rs-checkHelp">{FIELD_GUIDE_BY_KEY.supports_api.howToFill}</div>
                    <div className="rs-checkHelp">{FIELD_GUIDE_BY_KEY.supports_api.warning}</div>
                  </div>

                  <div className="rs-checkCard">
                    <label className="rs-checkHeader">
                      <input
                        type="checkbox"
                        checked={Boolean(formState.supports_reply_tracking)}
                        onChange={(event) =>
                          setFormState((current) => ({
                            ...current,
                            supports_reply_tracking: event.target.checked,
                          }))
                        }
                      />
                      <span>supports_reply_tracking</span>
                    </label>
                    <div className="rs-checkHelp">
                      {FIELD_GUIDE_BY_KEY.supports_reply_tracking.purpose}
                    </div>
                    <div className="rs-checkHelp">
                      {FIELD_GUIDE_BY_KEY.supports_reply_tracking.howToFill}
                    </div>
                    <div className="rs-checkHelp">
                      {FIELD_GUIDE_BY_KEY.supports_reply_tracking.warning}
                    </div>
                  </div>
                </div>

                <div className="rs-helperItem">
                  <strong>{isEditing ? "Historical usage." : "Recommended workflow."}</strong>{" "}
                  {isEditing
                    ? `Reviews linked: ${formState.reviews_count || 0}. Import runs linked: ${
                        formState.import_runs_count || 0
                      }. ${
                        formState.can_be_deleted
                          ? "Todavía puede borrarse porque no tiene relaciones históricas."
                          : "No debe borrarse; si deja de usarse, marca is_active = false."
                      }`
                    : "Da de alta primero las fuentes maestras y después conecta importaciones y procesos analíticos usando estos IDs estables."}
                </div>

                <div className="rs-modalActions">
                  <button type="button" className="rs-btn rs-btn--ghost" onClick={closeFormModal}>
                    Cancel
                  </button>
                  <button type="submit" className="rs-btn rs-btn--primary" disabled={saving}>
                    {saving ? "Saving..." : isEditing ? "Save changes" : "Create source"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {deleteTarget ? (
        <div className="rs-modalOverlay" onClick={closeDeleteModal}>
          <div
            className="rs-modal"
            style={{ width: "min(680px, 100%)" }}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="rs-modalHeader">
              <div>
                <h3 className="rs-modalTitle">Delete review source</h3>
                <div className="rs-modalSubtitle">
                  Solo debe borrarse una fuente si nunca se ha usado. Si ya tiene historia asociada,
                  la opción correcta es desactivarla.
                </div>
              </div>
              <button type="button" className="rs-modalClose" onClick={closeDeleteModal}>
                ×
              </button>
            </div>

            <form onSubmit={handleDelete}>
              <div className="rs-modalBody">
                {deleteError ? <div className="rs-banner rs-banner--error">{deleteError}</div> : null}
                <div className="rs-helperItem">
                  <strong>Source selected:</strong> {deleteTarget.name} ({deleteTarget.code}).
                </div>
                <div className="rs-helperItem">
                  <strong>Historical usage:</strong> {deleteTarget.reviews_count || 0} linked reviews
                  and {deleteTarget.import_runs_count || 0} import runs.
                </div>
                <div className="rs-modalActions">
                  <button type="button" className="rs-btn rs-btn--ghost" onClick={closeDeleteModal}>
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="rs-btn rs-btn--danger"
                    disabled={deleting || !deleteTarget.can_be_deleted}
                  >
                    {deleting ? "Deleting..." : "Delete source"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default ReviewSourcesAdmin;
